import React, { useState, useEffect } from "react";
import {
  Card,
  InputNumber,
  Button,
  message,
  Tabs,
  Typography,
  Row,
  Col,
  Table,
  Select,
  Image,
  Space,
  Divider,
} from "antd";
import { SaveOutlined, CalculatorOutlined, LineChartOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const MultipleServerModel = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Store the full response from backend
  const [resultData, setResultData] = useState(null);

  const [inputData, setInputData] = useState({
    name: fileName || "Untitled Multiple-Server Analysis",
    arrival_rate: 10,
    service_rate: 4,
    servers: 3,
    time_unit: "hours",
    cost_option: "use costs",
    server_cost: 50,
    waiting_cost: 20,
  });

  const TIME_UNIT_OPTIONS = [
    { value: "seconds", label: "Seconds" },
    { value: "minutes", label: "Minutes" },
    { value: "hours", label: "Hours" },
    { value: "days", label: "Days" },
  ];

  const COST_OPTIONS = [
    { value: "no costs", label: "No Costs" },
    { value: "use costs", label: "Use Costs" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (fileId) {
      fetchAnalysis();
    } else if (initialConfig) {
      setInputData((prev) => ({
        ...prev,
        ...initialConfig,
        name: initialConfig.name || prev.name,
      }));
    }
  }, [fileId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `/api/retrieve_Multiple_Server_Model/${fileId}/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { input_data, output_data, chart_url, name } = response.data;

      const restoredInput = {
        name: name || fileName,
        arrival_rate: input_data.arrival_rate,
        service_rate: input_data.service_rate,
        servers: input_data.servers,
        time_unit: input_data.time_unit,
        cost_option: input_data.cost_option,
        server_cost: input_data.server_cost,
        waiting_cost: input_data.waiting_cost,
      };

      setInputData(restoredInput);
      setResultData({ output_data, chart_url });
      setIsSaved(true);
      setActiveTab("2");
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to fetch analysis details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSolve = async () => {
    if (
      inputData.arrival_rate === null ||
      inputData.service_rate === null ||
      inputData.servers === null
    ) {
      message.error("Please fill in all required inputs correctly.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: inputData.name,
        input_data: {
          arrival_rate: inputData.arrival_rate,
          service_rate: inputData.service_rate,
          servers: inputData.servers,
          time_unit: inputData.time_unit,
          cost_option: inputData.cost_option,
          server_cost: inputData.server_cost,
          waiting_cost: inputData.waiting_cost,
        },
      };

      let response;
      if (fileId) {
        response = await axios.put(
          `/api/update_Multiple_Server_Model/${fileId}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          "/api/save_Multiple_Server_Model/",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Ensure the frontend updates selectedFile.id after saving
        if (setSelectedFile) {
          setSelectedFile({
            id: response.data.id,
            name: response.data.name,
            type: "multiple_server_model",
          });
        }
      }

      setResultData({
        output_data: response.data.output_data,
        chart_url: response.data.chart_url,
      });

      setIsSaved(true);
      message.success("Analysis saved and solved successfully");
      setActiveTab("2"); // Switch to results tab
    } catch (error) {
      console.error("Solve error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to solve analysis");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOutputTable = () => {
    if (!resultData || !resultData.output_data) return null;

    const { output_data } = resultData;

    // Core Metrics
    const coreMetricsKeys = [
      "server_utilization",
      "average_number_in_queue_Lq",
      "average_number_in_system_L",
      "average_time_in_queue_Wq",
      "average_time_in_system_W",
      "probability_system_empty_P0",
      "Wq_converted_1",
      "Wq_converted_2",
      "W_converted_1",
      "W_converted_2",
      "total_cost",
      "total_cost_rate"
    ];

    const formatMetricName = (key) => {
      const nameMap = {
        "server_utilization": "Server Utilization (ρ)",
        "average_number_in_queue_Lq": "Average number in queue (Lq)",
        "average_number_in_system_L": "Average number in system (L)",
        "average_time_in_queue_Wq": `Average time in queue (Wq) [${inputData.time_unit}]`,
        "average_time_in_system_W": `Average time in system (W) [${inputData.time_unit}]`,
        "probability_system_empty_P0": "Probability system empty (P0)",
        "Wq_converted_1": "Wq Converted 1",
        "Wq_converted_2": "Wq Converted 2",
        "W_converted_1": "W Converted 1",
        "W_converted_2": "W Converted 2",
        "total_cost": "Total Cost",
        "total_cost_rate": "Total Cost Rate",
      };
      return nameMap[key] || key.replace(/_/g, ' ');
    };

    const tableData = coreMetricsKeys
      .filter((key) => output_data[key] !== undefined)
      .map((key, index) => ({
        key: index,
        metric: formatMetricName(key),
        value: typeof output_data[key] === "number" ? output_data[key].toFixed(4) : output_data[key],
      }));

    const columns = [
      { title: "Metric", dataIndex: "metric", key: "metric", render: (text) => <Text strong>{text}</Text> },
      { title: "Value", dataIndex: "value", key: "value" },
    ];

    // Probability Table Arrays
    const probTableColumns = [
      { title: "k", dataIndex: "k", key: "k" },
      { title: "Prob(n=k) [P_k]", dataIndex: "P_k", key: "P_k" },
      { title: "Prob(n≤k) [P_le_k]", dataIndex: "P_le_k", key: "P_le_k" },
      { title: "Prob(n>k) [P_gt_k]", dataIndex: "P_gt_k", key: "P_gt_k" },
    ];

    // Sensitivity Table Arrays
    const sensTableColumns = [
      { title: "Servers", dataIndex: "servers", key: "servers" },
      { title: "ρ", dataIndex: "rho", key: "rho" },
      { title: "Lq", dataIndex: "Lq", key: "Lq" },
      { title: "L", dataIndex: "L", key: "L" },
      { title: "Wq", dataIndex: "Wq", key: "Wq" },
      { title: "W", dataIndex: "W", key: "W" },
    ];

    // Cost Table Arrays
    const costTableColumns = [
      { title: "Servers", dataIndex: "servers", key: "servers" },
      { title: "Total Cost Waiting", dataIndex: "total_cost_waiting", key: "total_cost_waiting" },
      { title: "Total Cost System", dataIndex: "total_cost_system", key: "total_cost_system" },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Operational Metrics" size="small">
              <Table
                dataSource={tableData}
                columns={columns}
                pagination={false}
                bordered
                size="small"
              />
            </Card>
          </Col>
          {resultData?.chart_url && (
            <Col xs={24} md={12}>
              <Card title="Traffic Map / Flow Analysis" size="small" style={{ textAlign: "center" }}>
                <Image
                  src={resultData.chart_url.startsWith('http') ? resultData.chart_url : `http://127.0.0.1:8000${resultData.chart_url}`}
                  alt="Multiple-Server Flow Graph"
                  fallback="https://via.placeholder.com/400x300?text=Chart+Not+Available"
                  style={{ maxHeight: '300px' }}
                />
              </Card>
            </Col>
          )}
        </Row>

        {output_data.probability_table && (
          <Card title="Probability Table" size="small">
            <Table
              dataSource={output_data.probability_table.map((row, i) => ({ ...row, key: i }))}
              columns={probTableColumns}
              pagination={false}
              bordered
              size="small"
              scroll={{ y: 240 }}
            />
          </Card>
        )}

        {output_data.sensitivity_table && (
          <Card title="Sensitivity Table" size="small">
            <Table
              dataSource={output_data.sensitivity_table.map((row, i) => ({ ...row, key: i }))}
              columns={sensTableColumns}
              pagination={false}
              bordered
              size="small"
            />
          </Card>
        )}

        {output_data.cost_table && (
          <Card title="Cost Table" size="small">
            <Table
              dataSource={output_data.cost_table.map((row, i) => ({ ...row, key: i }))}
              columns={costTableColumns}
              pagination={false}
              bordered
              size="small"
            />
          </Card>
        )}
      </Space>
    );
  };

  return (
    <Card
      title={<Title level={4}><CalculatorOutlined /> {inputData.name}</Title>}
      className="calculator-card"
      style={{ borderRadius: '12px', overflow: 'hidden', padding: '20px' }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
        <TabPane tab={<span><TableOutlined /> Inputs</span>} key="1">
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Card title="Parameters" size="small">
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Arrival Rate (λ):</Text>
                  <InputNumber
                    value={inputData.arrival_rate}
                    onChange={(val) => setInputData({ ...inputData, arrival_rate: val })}
                    min={0.0001}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Service Rate (μ):</Text>
                  <InputNumber
                    value={inputData.service_rate}
                    onChange={(val) => setInputData({ ...inputData, service_rate: val })}
                    min={0.0001}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
                <div>
                  <Text strong>Number of Servers:</Text>
                  <InputNumber
                    value={inputData.servers}
                    onChange={(val) => setInputData({ ...inputData, servers: val })}
                    min={1}
                    precision={0}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Settings" size="small">
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Time Unit:</Text>
                  <Select
                    value={inputData.time_unit}
                    onChange={(val) => setInputData({ ...inputData, time_unit: val })}
                    style={{ width: "100%", marginTop: 8 }}
                    options={TIME_UNIT_OPTIONS}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Cost Option:</Text>
                  <Select
                    value={inputData.cost_option}
                    onChange={(val) => setInputData({ ...inputData, cost_option: val })}
                    style={{ width: "100%", marginTop: 8 }}
                    options={COST_OPTIONS}
                  />
                </div>
                {inputData.cost_option === "use costs" && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Server Cost:</Text>
                      <InputNumber
                        value={inputData.server_cost}
                        onChange={(val) => setInputData({ ...inputData, server_cost: val })}
                        min={0}
                        style={{ width: "100%", marginTop: 8 }}
                      />
                    </div>
                    <div>
                      <Text strong>Waiting Cost:</Text>
                      <InputNumber
                        value={inputData.waiting_cost}
                        onChange={(val) => setInputData({ ...inputData, waiting_cost: val })}
                        min={0}
                        style={{ width: "100%", marginTop: 8 }}
                      />
                    </div>
                  </>
                )}
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSolve}
              loading={loading}
              block
              style={{ height: '50px' }}
            >
              {isSaved ? "Update Analysis" : "Calculate & Save"}
            </Button>
          </div>
        </TabPane>

        <TabPane tab={<span><LineChartOutlined /> Results</span>} key="2" disabled={!resultData}>
          {renderOutputTable()}
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default MultipleServerModel;
